import { Controller, ControllerProps, FieldValues } from "react-hook-form";
import { Text } from "react-native";
import { View } from "ui/atoms/view";
import {
  Checkbox,
  CheckboxGroup,
  CheckboxIcon,
  CheckboxIndicator,
  CheckboxLabel,
} from "ui/gluestack/checkbox";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlHelper,
  FormControlHelperText,
} from "ui/gluestack/form-control";
import { AlertCircleIcon, CircleIcon } from "ui/gluestack/icon";
import { requiredValidation } from "ui/utils/Validators";
import { FormControlledInput } from "./FormControlledInput";
export interface Props<TFieldValues extends FieldValues = FieldValues> {
  id: string;
  name: ControllerProps<TFieldValues>["name"];
  otherTextName?: ControllerProps<TFieldValues>["name"];
  options: {
    label: string;
    value: string;
    key?: string;
    hasDescription?: boolean;
    isDisabled?: boolean;
  }[];
  label?: string;
  secondaryLabel?: boolean;
  placeholder?: string;
  helperText?: string;
  invalidMessage?: string;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
  isInvalid?: boolean;
  autoFocus?: boolean;
  control: ControllerProps<TFieldValues>["control"];
  rules?: ControllerProps<TFieldValues>["rules"];
  className?: string;
}

export function FormControlledRadioGroup<
  TFieldValues extends FieldValues = FieldValues,
>({
  id,
  name,
  otherTextName,
  isInvalid,
  isReadOnly,
  isDisabled,
  isRequired,
  label,
  secondaryLabel,
  invalidMessage,
  helperText,
  control,
  rules,
  options,
  className,
}: Props<TFieldValues>) {
  const localRules = {
    required: isRequired ? requiredValidation(label ?? "Field") : undefined,
    ...rules,
  };

  return (
    <Controller
      control={control}
      name={name}
      render={({ fieldState: { error }, field: { value, onChange } }) => (
        <FormControl
          isInvalid={isInvalid || !!error}
          size="md"
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
          isRequired={isRequired}
        >
          {label ? (
            <Text
              className={`test-sm  mb-2 ${secondaryLabel ? "text-gray-500" : "text-gray-600 font-medium"}`}
            >
              {label}
              {!isRequired ? " (Optional)" : ""}
            </Text>
          ) : null}
          <View className={`relative my-1 fill-white ${className}`}>
            <CheckboxGroup
              id={id}
              onChange={(value?: string[]) => {
                onChange(!value?.length ? [] : [value[value.length - 1]]);
              }}
              value={value}
              className="gap-4"
              aria-label={label}
            >
              {options.map((option) => (
                <>
                  <Checkbox
                    size="sm"
                    value={option.value}
                    key={option.key ?? option.value}
                    aria-label={option.label}
                    isDisabled={option.isDisabled}
                  >
                    <CheckboxIndicator className="rounded-full">
                      <CheckboxIcon as={CircleIcon} />
                    </CheckboxIndicator>
                    <CheckboxLabel>{option.label}</CheckboxLabel>
                  </Checkbox>

                  {option.hasDescription && otherTextName ? (
                    <FormControlledInput
                      control={control}
                      name={otherTextName}
                      id={`${id}-other`}
                      isDisabled={!value.includes(option.value)}
                    ></FormControlledInput>
                  ) : null}
                </>
              ))}
            </CheckboxGroup>
          </View>
          {helperText ? (
            <FormControlHelper>
              <FormControlHelperText className="text-gray-500">
                {helperText ?? " "}
              </FormControlHelperText>
            </FormControlHelper>
          ) : null}
          {invalidMessage || error?.message ? (
            <FormControlError>
              <FormControlErrorIcon
                as={AlertCircleIcon}
                className="text-error-500"
                size="sm"
              />
              <FormControlErrorText className="text-error-500 text-sm">
                {error?.message || invalidMessage}
              </FormControlErrorText>
            </FormControlError>
          ) : null}
        </FormControl>
      )}
      rules={localRules}
    />
  );
}
